import { API_URL } from '../api.js';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Avatar from '../components/Avatar';
import styles from './MembersTab.module.css';
import { Settings, Share2, Bell, User, Copy, ExternalLink, Shield, Award, Sparkles, Globe, X } from 'lucide-react';

const MODAL_TABS = ['Invite', 'Notifications', 'Membership'];

// Default notification preferences
const DEFAULT_NOTIF_PREFS = {
  newPost:    { following: true,  email: false },
  likes:      { following: true,  email: false },
  comments:   { following: true,  email: true  },
  newMember:  { following: false, email: false },
  liveEvent:  { following: true,  email: true  },
};

function getRequiredLevelForPerk(levelConfigsJson, keyword, defaultLevel) {
  if (!levelConfigsJson) return defaultLevel;
  try {
    const decoded = JSON.parse(levelConfigsJson);
    if (Array.isArray(decoded)) {
      for (const lvlObj of decoded) {
        const perks = lvlObj.perks;
        if (Array.isArray(perks)) {
          for (const perk of perks) {
            if (perk.toLowerCase().includes(keyword.toLowerCase())) {
              return lvlObj.level || defaultLevel;
            }
          }
        }
      }
    }
  } catch (e) {
    console.error("Error parsing levelConfigs for perk", e);
  }
  return defaultLevel;
}

export default function MembersTab({ community, onToast = () => {}, onChatOpen = () => {}, activeMembership, searchQuery = '' }) {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const isOwnerOrAdmin = user?.role === 'admin' || Number(user?.id) === Number(community?.ownerId) || activeMembership?.role === 'admin';
  // Compute the required level for DM from community levelConfigs
  const dmRequiredLevel = getRequiredLevelForPerk(community?.levelConfigs, 'messaging', 2);
  const userLevel       = activeMembership?.level ?? 1;
  // Admins and owners always have DM unlocked
  const hasDmUnlocked   = isOwnerOrAdmin || userLevel >= dmRequiredLevel;

  const [filter, setFilter]   = useState('all');
  const [search, setSearch]   = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalTab, setModalTab]   = useState('Invite');
  const [notifPrefs, setNotifPrefs] = useState(DEFAULT_NOTIF_PREFS);
  const [linkCopied, setLinkCopied] = useState(false);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const isOwner = Number(user?.id) === Number(community?.ownerId);

  useEffect(() => {
    setShowLeaveConfirm(false);
  }, [showModal, modalTab]);

  async function handleLeave() {
    if (isOwner) {
      onToast("As the community owner, you cannot leave the community. You must transfer ownership or delete the community instead.");
      return;
    }
    setLeaving(true);
    try {
      const res = await fetch(`${API_URL}/api/communities/${community.id}/leave`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (res.ok) {
        onToast("You have left the community.");
        setShowModal(false);
        navigate('/');
      } else {
        const errData = await res.json().catch(() => ({}));
        onToast(errData.message || "Failed to leave the community.");
      }
    } catch (err) {
      console.error(err);
      onToast("An error occurred while trying to leave the community.");
    } finally {
      setLeaving(false);
    }
  }

  useEffect(() => {
    if (!community?.id) return;
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_URL}/api/communities/${community.id}/members`, { headers })
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        const mapped = (data || []).map(m => ({
          id: m.id,
          name: m.displayName || '',
          avatarUrl: m.avatarUrl || null,
          level: m.level || 1,
          xp: m.xp || 0,
          handle: '@' + (m.displayName || '').split(' ')[0].toLowerCase() + m.id,
          bio: m.bio || 'Community member',
          isOnline: m.isOnline === true,
          isAdmin: m.role === 'admin',
          joinDate: m.joinedAt
            ? new Date(m.joinedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            : 'Recently',
          location: '',
        }));
        setMembers(mapped);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [community?.id, token]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const filteredMembers = members.filter(m => {
    if (filter === 'admins' && !m.isAdmin)   return false;
    if (filter === 'online' && !m.isOnline)  return false;
    if (search && !(m.name || '').toLowerCase().includes(search.toLowerCase()) && !(m.handle || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (searchQuery) {
      const qGlobal = searchQuery.toLowerCase().trim();
      const name = (m.name || '').toLowerCase();
      const handle = (m.handle || '').toLowerCase();
      const bio = (m.bio || '').toLowerCase();
      if (!name.includes(qGlobal) && !handle.includes(qGlobal) && !bio.includes(qGlobal)) return false;
    }
    return true;
  });

  const adminsCount = members.filter(m => m.isAdmin).length;
  const onlineCount = members.filter(m => m.isOnline).length;

  function copyLink() {
    const slug = community?.slug || 'community';
    navigator.clipboard?.writeText(`https://www.qeduvo.com/${slug}/join`).catch(() => {});
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  }

  function togglePref(key, field) {
    setNotifPrefs(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: !prev[key][field] }
    }));
  }

  const NOTIF_ROWS = [
    { key: 'newPost',   label: 'New post from followed admin' },
    { key: 'comments',  label: 'Comments on my posts' },
    { key: 'likes',     label: 'Likes on my posts' },
    { key: 'newMember', label: 'New members joining' },
    { key: 'liveEvent', label: 'Live events starting' },
  ];

  return (
    <div className={styles.membersContainer}>
      <div className={styles.mainContent}>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.filters}>
            {[
              { key: 'all',    label: 'Members', count: members.length },
              { key: 'admins', label: 'Admins',  count: adminsCount },
              { key: 'online', label: 'Online',  count: onlineCount },
            ].map(f => (
              <button key={f.key} className={`${styles.filterBtn} ${filter === f.key ? styles.active : ''}`} onClick={() => setFilter(f.key)}>
                {f.label} <span className={styles.countBadge}>{f.count}</span>
              </button>
            ))}
          </div>
          <div className={styles.headerActions}>
            <button className="btn btn-primary btn-sm" onClick={() => { setShowModal(true); setModalTab('Invite'); }}>INVITE</button>
          </div>
        </div>

        {/* Search */}
        <div className={styles.searchBar}>
          <span className={styles.searchIcon}>🔍</span>
          <input type="text" placeholder="Search members" value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {/* Member List */}
        <div className={styles.memberList}>
          {filteredMembers.map(member => (
            <div key={member.id} className={styles.memberCard}>
              <div className={styles.memberAvatarArea}>
                <div className={styles.avatar}>
                  {member.avatarUrl
                    ? <img src={`${API_URL}${member.avatarUrl}`} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                    : (member.name?.[0]?.toUpperCase() || '?')}
                </div>
                <div className={styles.levelBadge}>{member.level}</div>
              </div>
              <div className={styles.memberInfo}>
                <div className={styles.memberNameLine}>
                  <span className={styles.memberName}>{member.name}</span>
                  {member.isAdmin && <span className={styles.adminBadge} title="Admin">💎</span>}
                  {member.isOnline && <span style={{ fontSize:10, color:'var(--accent3)', marginLeft:4 }}>🔥</span>}
                </div>
                <div className={styles.memberHandle}>{member.handle}</div>
                <div className={styles.memberBio}>{member.bio}</div>
                <div className={styles.memberMeta}>
                  {member.isOnline
                    ? <span className={styles.onlineStatus}><span className={styles.dot}></span> Online now</span>
                    : <span className={styles.offlineStatus}>Last seen recently</span>}
                  <span className={styles.metaDivider}>•</span>
                  <span>Joined {member.joinDate}</span>
                  {member.location && <><span className={styles.metaDivider}>•</span><span>📍 {member.location}</span></>}
                </div>
              </div>
              <div className={styles.memberActions}>
                {String(member.id) !== String(user?.id) && (
                  <button
                    className={`${styles.chatBtn} ${!hasDmUnlocked ? styles.chatBtnLocked : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!hasDmUnlocked) {
                        onToast(`🔒 Direct Messaging unlocks at Level ${dmRequiredLevel}. You are currently Level ${userLevel}.`);
                        return;
                      }
                      onChatOpen({ id: member.id, displayName: member.name, avatarUrl: member.avatarUrl });
                    }}
                    title={hasDmUnlocked ? `Chat with ${member.name}` : `Unlock at Level ${dmRequiredLevel} (you are Level ${userLevel})`}
                  >
                    {hasDmUnlocked ? (
                      <>💬 <span>Message</span></>
                    ) : (
                      <>
                        🔒
                        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
                          <span style={{ fontSize: 10, fontWeight: 800 }}>LOCKED</span>
                          <span style={{ fontSize: 9, opacity: 0.7 }}>Lvl {dmRequiredLevel} required</span>
                        </span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
          {filteredMembers.length === 0 && (
            <div className={styles.noResults}>
              {searchQuery.trim() ? "No members found matching that search term." : "No members found."}
            </div>
          )}
        </div>
      </div>

      {/* Sidebar */}
      <div className={styles.sidebar}>
        <div className="card" style={{ marginBottom: 20, overflow: 'hidden' }}>
          <div style={{
            height: 100,
            background: community?.coverImageUrl
              ? `url(${API_URL}${community.coverImageUrl}) center/cover no-repeat`
              : 'linear-gradient(45deg, #0f172a, #1e293b)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white'
          }}>
            {!community?.coverImageUrl && (
              <>
                <div style={{ fontSize: 16, fontWeight: 900, letterSpacing: 1, marginBottom: 2, color: '#38bdf8' }}>{(community?.name || 'Community').toUpperCase()}</div>
                <div style={{ fontSize: 12, fontWeight: 800 }}>WELCOME TO OUR COMMUNITY</div>
              </>
            )}
          </div>
          <div className="card-body">
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, color: 'var(--text)' }}>{community?.name || 'Community'}</h3>
            <p className="text-muted" style={{ fontSize: 11, marginBottom: 12 }}>qeduvo.com/{community?.slug || 'url'}</p>
            <p style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 16, color: 'var(--text2)' }}>
              {community?.description || 'Welcome! Discover resources and connect with members in our community.'}
            </p>
            
            {/* Stats Row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 20 }}>
              {[
                { label: 'Members', value: members.length, color: 'var(--accent)' },
                { label: 'Online', value: onlineCount, color: 'var(--accent3)' },
                { label: 'Admins', value: adminsCount, color: 'var(--accent4)' }
              ].map(stat => (
                <div key={stat.label} style={{ 
                  background: 'var(--surface2)', 
                  border: '1px solid var(--border)', 
                  borderRadius: 14, 
                  padding: '12px 6px', 
                  textAlign: 'center',
                  transition: 'all 0.2s ease'
                }}>
                  <div style={{ fontSize: 18, fontWeight: 900, color: stat.color }}>{stat.value}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, marginTop: 2 }}>{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Avatar stack */}
            {members.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                {members.slice(0, 6).map((m, i) => (
                  <div key={m.id ?? i} style={{ marginLeft: i > 0 ? -8 : 0, zIndex: 6 - i }}>
                    <Avatar
                      user={{
                        avatar:      m.avatarUrl,
                        displayName: m.name,
                      }}
                      size={28}
                    />
                  </div>
                ))}
                {members.length > 6 && (
                  <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 700, color: 'var(--text3)' }}>
                    +{(members.length - 6).toLocaleString()}
                  </span>
                )}
              </div>
            )}

            {/* Settings button */}
            <button className="btn btn-outline" style={{ width: '100%', borderRadius: 12, padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              onClick={() => { setShowModal(true); setModalTab('Notifications'); }}>
              <Settings size={15} />
              <span>Settings & Preferences</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Settings Modal ── */}
      {showModal && (
        <div className="modal-overlay" style={{ backdropFilter: 'blur(8px)', background: 'rgba(2, 6, 23, 0.65)' }} onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 800, width: '95vw', borderRadius: 24, overflow: 'hidden', border: '1px solid var(--border)', position: 'relative' }} onClick={e => e.stopPropagation()}>
            <button 
              type="button"
              className="qeduvo-icon-btn" 
              onClick={() => setShowModal(false)} 
              style={{ 
                position: 'absolute', 
                top: 16, 
                right: 16, 
                zIndex: 50, 
                width: 32, 
                height: 32,
                borderRadius: '50%',
                border: 'none',
                background: 'var(--surface2)',
                color: 'var(--text2)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X size={16} />
            </button>

            <div className="modal-body" style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: 0 }}>

              {/* Tab Navigation Pill bar */}
              <div className="modal-tabbar" style={{ display: 'flex', background: 'var(--surface2)', padding: 5, borderRadius: 16, marginBottom: 24, gap: 4 }}>
                {[
                  { id: 'Invite', label: 'Invite Referrals', icon: Share2 },
                  { id: 'Notifications', label: 'Notifications', icon: Bell },
                  { id: 'Membership', label: 'Membership Detail', icon: User }
                ].map(tab => (
                  <button key={tab.id} className="modal-tab" onClick={() => setModalTab(tab.id)}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      border: 'none',
                      cursor: 'pointer',
                      padding: '10px 14px',
                      fontSize: 13,
                      fontWeight: 700,
                      borderRadius: 12,
                      background: modalTab === tab.id ? 'var(--surface)' : 'transparent',
                      color: modalTab === tab.id ? 'var(--accent)' : 'var(--text3)',
                      boxShadow: modalTab === tab.id ? 'var(--shadow-sm)' : 'none',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                    <tab.icon size={14} />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>

              {/* ── Invite Tab ── */}
              {modalTab === 'Invite' && (
                <div style={{ animation: 'fadeIn 0.2s ease' }} className="flex-col" style={{ gap: 18 }}>
                  <div style={{ background: 'var(--accent-soft)', border: '1px solid var(--accent)', borderRadius: 16, padding: '18px 20px', display: 'flex', gap: 14, alignItems: 'center' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--accent-grad)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Sparkles size={22} />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--accent)' }}>Invite Friends & Earn Commission</div>
                      <div style={{ fontSize: 12.5, color: 'var(--text2)', marginTop: 2 }}>
                        Earn <span style={{ color: 'var(--accent3)', fontWeight: 700 }}>40% recurring commission</span> for life when you invite members.
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="form-label" style={{ marginBottom: 6 }}>Referral Invite Link</label>
                    <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 12, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input type="text" readOnly value={`https://www.qeduvo.com/${community?.slug || 'community'}/join`}
                        style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', outline: 'none', fontSize: 13, color: 'var(--text2)', fontFamily: 'monospace', textOverflow: 'ellipsis' }} />
                      <button onClick={copyLink} className="btn btn-primary btn-sm"
                        style={{ padding: '8px 16px', borderRadius: 8, background: linkCopied ? 'var(--accent3)' : 'var(--accent-grad)' }}>
                        {linkCopied ? 'Copied ✓' : <><Copy size={13} /> Copy</>}
                      </button>
                    </div>
                  </div>

                  <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: '20px', textAlign: 'center' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Your Referral Performance</div>
                    <div style={{ fontSize: 13.5, color: 'var(--text3)', padding: '20px 0', border: '1px dashed var(--border)', borderRadius: 12 }}>
                      No referrals registered yet. Share your invite link to get started!
                    </div>
                  </div>
                </div>
              )}

              {/* ── Notifications Tab ── */}
              {modalTab === 'Notifications' && (
                <div style={{ animation: 'fadeIn 0.2s ease' }} className="flex-col" style={{ gap: 18 }}>
                  <div>
                    <h4 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700 }}>Configure Alerts</h4>
                    <p style={{ fontSize: 12.5, color: 'var(--text3)', margin: 0 }}>Choose exactly what updates trigger notifications and emails.</p>
                  </div>

                  <div style={{ border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden', background: 'var(--surface)' }}>
                    {/* Table Header Row */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px', padding: '12px 20px', background: 'var(--surface2)', fontSize: 11, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid var(--border)' }}>
                      <div>Activity Event</div>
                      <div style={{ textAlign: 'center' }}>Alerts</div>
                      <div style={{ textAlign: 'center' }}>Email</div>
                    </div>

                    {NOTIF_ROWS.map((row, i) => (
                      <div key={row.key} style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px', padding: '14px 20px', alignItems: 'center', borderBottom: i < NOTIF_ROWS.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>{row.label}</div>
                        {['following', 'email'].map(field => {
                          const val = notifPrefs[row.key]?.[field];
                          return (
                            <div key={field} style={{ display: 'flex', justifyContent: 'center' }}>
                              <button 
                                type="button"
                                onClick={() => togglePref(row.key, field)}
                                style={{ 
                                  width: 44, 
                                  height: 24, 
                                  borderRadius: 12, 
                                  border: 'none', 
                                  cursor: 'pointer', 
                                  transition: 'all 0.2s ease',
                                  background: val ? 'var(--accent)' : 'var(--surface3)', 
                                  position: 'relative' 
                                }}
                              >
                                <div style={{ 
                                  width: 18, 
                                  height: 18, 
                                  borderRadius: '50%', 
                                  background: '#fff', 
                                  position: 'absolute', 
                                  top: 3,
                                  left: val ? 23 : 3, 
                                  transition: 'left 0.2s cubic-bezier(0.4, 0, 0.2, 1)' 
                                }} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>

                  <button className="btn btn-primary" style={{ width: '100%', padding: '12px', borderRadius: 12, fontWeight: 800, marginTop: 8 }} onClick={() => { onToast('Preferences saved'); setShowModal(false); }}>
                    Save Preferences
                  </button>
                </div>
              )}

              {/* ── Membership Tab ── */}
              {modalTab === 'Membership' && (
                <div style={{ animation: 'fadeIn 0.2s ease' }} className="flex-col" style={{ gap: 20 }}>
                  
                  {/* Membership Card Details */}
                  <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 18, padding: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                      <div style={{ 
                        width: 52, 
                        height: 52, 
                        borderRadius: 14, 
                        background: 'linear-gradient(135deg, var(--accent) 0%, #7c3aed 100%)', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        fontSize: 20, 
                        color: '#fff',
                        boxShadow: '0 8px 16px -6px var(--accent)'
                      }}>
                        🎓
                      </div>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 16.5, color: 'var(--text)' }}>{community?.name || 'Community'}</div>
                        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2, fontFamily: 'monospace' }}>qeduvo.com/{community?.slug || 'url'}</div>
                      </div>
                      <div style={{ marginLeft: 'auto', background: 'var(--accent-soft)', color: 'var(--accent)', fontSize: 11, fontWeight: 800, padding: '5px 12px', borderRadius: 10 }}>ACTIVE</div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      {[
                        { label: 'Member Since', val: 'Recently' },
                        { label: 'Plan Subscription', val: 'Free Access' },
                        { label: 'Profile Authority', val: user?.id === community?.ownerId ? 'Community Owner' : 'Member' },
                        { label: 'Accumulated Level', val: `Level ${user?.level || 1} · ${(user?.xp || 0).toLocaleString()} XP` }
                      ].map(item => (
                        <div key={item.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px' }}>
                          <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{item.label}</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{item.val}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions list */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <button className="btn btn-primary" style={{ padding: '14px 20px', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>Upgrade Subscription</span>
                      <Sparkles size={16} />
                    </button>
                    <button className="btn btn-outline" style={{ padding: '14px 20px', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>External Account Settings</span>
                      <ExternalLink size={15} />
                    </button>
                    
                    {!showLeaveConfirm ? (
                      <button className="btn btn-ghost" style={{ padding: '14px 20px', borderRadius: 14, color: 'var(--accent2)', border: '1px solid var(--accent2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={() => {
                          if (isOwner) {
                            onToast("As the community owner, you cannot leave the community. You must transfer ownership or delete the community instead.");
                          } else {
                            setShowLeaveConfirm(true);
                          }
                        }}>
                        Leave Community Group
                      </button>
                    ) : (
                      <div style={{
                        background: 'rgba(239, 68, 68, 0.05)',
                        border: '1px dashed var(--accent2)',
                        borderRadius: 14,
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12,
                        animation: 'fadeIn 0.2s ease'
                      }}>
                        <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600, textAlign: 'center' }}>
                          Are you sure you want to leave this community group? You will lose all your accumulated levels and XP.
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                          <button className="btn btn-outline" style={{ flex: 1, padding: '10px', borderRadius: 10 }}
                            onClick={() => setShowLeaveConfirm(false)} disabled={leaving}>
                            Cancel
                          </button>
                          <button className="btn" style={{ flex: 1, padding: '10px', borderRadius: 10, background: 'var(--accent2)', color: '#fff', border: 'none', fontWeight: 700 }}
                            onClick={handleLeave} disabled={leaving}>
                            {leaving ? 'Leaving…' : 'Leave Group'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
